/**
 * Generate panel — idea card (prompt + optional reference), style chips, Design Brief (Phase 11).
 * Print original as-is is a secondary action on the same pane (not a peer tab).
 */

/**
 * @param {HTMLElement} root
 * @param {{ getState: () => Record<string, unknown>, setState: (patch: Record<string, unknown>) => void, subscribe: (fn: (state: Record<string, unknown>) => void) => void }} store
 * @param {(payload: {
 *   prompt: string,
 *   style_code: string|null,
 *   side: 'front'|'back',
 *   file: File|null,
 *   rightsConfirmed: boolean,
 *   brief?: Record<string, unknown>|null
 * }) => Promise<void>} onGenerate
 * @param {(payload: { file: File, side: 'front'|'back', rightsConfirmed: boolean }) => Promise<void>} [onPrintAsIs]
 * @param {(payload: {
 *   prompt: string,
 *   style_code: string|null,
 *   side: 'front'|'back',
 *   has_reference: boolean
 * }) => Promise<Record<string, unknown>>} [onSuggestBrief]
 * @param {{ show: (owner: string, title: string, detail?: string) => void, hide: (owner: string) => void }} [overlay]
 */
export function bindGeneratePanel(root, store, onGenerate, onPrintAsIs, onSuggestBrief, overlay) {
    const pane = root.querySelector('[data-bag-pane="generate"]') || root;
    const promptEl = root.querySelector('#bag-prompt');
    const generateBtn = root.querySelector('#bag-generate-btn');
    const suggestBtn = root.querySelector('#bag-suggest-brief-btn');
    const quickBtn = root.querySelector('#bag-quick-generate-btn');
    const asisBtn = root.querySelector('#bag-asis-btn');
    const fileEl = root.querySelector('#bag-reference-file');
    const rightsEl = root.querySelector('#bag-reference-rights');
    const statusEl = root.querySelector('#bag-generate-status');
    const quotaEl = root.querySelector('#bag-quota-hint');
    const briefHost = root.querySelector('#bag-design-brief');
    const summaryEl = root.querySelector('#bag-brief-summary');
    const notesEl = root.querySelector('#bag-brief-notes');
    const plateEl = root.querySelector('#bag-brief-remove-plate');
    const briefEnabled = (pane.getAttribute('data-design-brief-enabled') || '0') === '1';
    const maxUploadMb = Math.max(1, parseFloat(root.getAttribute('data-upload-max-mb') || '8') || 8);
    const maxUploadBytes = Math.round(maxUploadMb * 1024 * 1024);
    const ideaCard = pane.querySelector('[data-bag-idea-card]');
    const attachBtn = pane.querySelector('[data-bag-idea-attach]');
    const removeBtn = pane.querySelector('[data-bag-idea-remove]');
    const thumbEl = pane.querySelector('[data-bag-idea-thumb]');
    const filenameEl = pane.querySelector('[data-bag-idea-filename]');
    const ideaErrorEl = pane.querySelector('[data-bag-idea-error]');
    const emptyNeedIdea = 'Add a description, a photo, or both.';
    /** @type {string|null} */
    let thumbUrl = null;

    /** @type {string|null} */
    let activeStyle = null;
    /** @type {Record<string, unknown>|null} */
    let currentBrief = null;
    let busy = false;
    /** @type {HTMLButtonElement|null} */
    let loadingBtn = null;

    root.querySelectorAll('[data-bag-style]').forEach((chip) => {
        chip.addEventListener('click', () => {
            const code = chip.getAttribute('data-bag-style');
            activeStyle = activeStyle === code ? null : code;
            root.querySelectorAll('[data-bag-style]').forEach((el) => {
                el.classList.toggle('is-active', el.getAttribute('data-bag-style') === activeStyle);
            });
        });
    });

    if (fileEl) {
        fileEl.addEventListener('change', () => {
            const file = selectedFile();
            if (file) {
                acceptFile(file, false);
                return;
            }
            clearPhoto();
        });
    }

    if (attachBtn && fileEl) {
        attachBtn.addEventListener('click', () => {
            if (attachBtn.disabled || busy) {
                return;
            }
            fileEl.click();
        });
    }

    if (removeBtn) {
        removeBtn.addEventListener('click', () => {
            if (removeBtn.disabled || busy) {
                return;
            }
            clearPhoto();
        });
    }

    bindIdeaCardDrag();

    if (briefHost) {
        briefHost.querySelectorAll('[data-brief-field]').forEach((group) => {
            group.querySelectorAll('[data-brief-value]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const field = group.getAttribute('data-brief-field');
                    const value = btn.getAttribute('data-brief-value');
                    if (!field || !value || !currentBrief || !currentBrief.plan) {
                        return;
                    }
                    /** @type {Record<string, unknown>} */ (currentBrief.plan)[field] = value;
                    paintBriefChips();
                });
            });
        });
    }

    if (suggestBtn && typeof onSuggestBrief === 'function') {
        suggestBtn.addEventListener('click', () => {
            if (suggestBtn.disabled || busy) {
                return;
            }
            runSuggest().catch((err) => {
                setStatus(err && err.message ? err.message : 'Could not suggest a design plan.', true);
            });
        });
    }

    if (quickBtn && typeof onSuggestBrief === 'function') {
        quickBtn.addEventListener('click', () => {
            if (quickBtn.disabled || busy) {
                return;
            }
            const prompt = promptEl ? String(promptEl.value || '').trim() : '';
            const file = selectedFile();
            if (!prompt && !file) {
                setStatus(emptyNeedIdea, true);
                return;
            }
            if (file && !assertFileWithinLimit(file)) {
                return;
            }
            if (file && (!rightsEl || !rightsEl.checked)) {
                setStatus('Please confirm you have the rights to use this image.', true);
                return;
            }
            const side = /** @type {'front'|'back'} */ (store.getState().activeSide || 'front');
            setBusy(true, {
                title: 'Planning then generating…',
                detail: 'Usually 30–90 seconds. Please stay on this page.',
                button: quickBtn,
                buttonLabel: 'Generating…',
            });
            setStatus('Planning then generating…', false);
            onSuggestBrief({
                prompt: prompt,
                style_code: activeStyle,
                side: side,
                has_reference: Boolean(file),
            })
                .then((brief) => {
                    applyBrief(brief);
                    return onGenerate({
                        prompt: prompt,
                        style_code: activeStyle,
                        side: side,
                        file: file,
                        rightsConfirmed: Boolean(file && rightsEl && rightsEl.checked),
                        brief: syncBriefFromDom() || brief,
                    });
                })
                .catch((err) => {
                    setStatus(err && err.message ? err.message : 'Quick generate failed.', true);
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    }

    if (generateBtn) {
        generateBtn.addEventListener('click', () => {
            if (generateBtn.disabled || busy) {
                return;
            }
            const prompt = promptEl ? String(promptEl.value || '').trim() : '';
            const file = selectedFile();
            if (!prompt && !file) {
                setStatus(emptyNeedIdea, true);
                return;
            }
            if (file && !assertFileWithinLimit(file)) {
                return;
            }
            if (file && (!rightsEl || !rightsEl.checked)) {
                setStatus('Please confirm you have the rights to use this image.', true);
                return;
            }
            if (briefEnabled && !currentBrief) {
                setStatus('Suggest a design plan first, or we can auto-plan on generate.', false);
            }
            const side = /** @type {'front'|'back'} */ (store.getState().activeSide || 'front');
            const briefPayload = briefEnabled ? syncBriefFromDom() : null;
            setBusy(true, {
                title: 'Creating your print…',
                detail: 'Usually 30–90 seconds. Please stay on this page.',
                button: generateBtn,
                buttonLabel: 'Generating…',
            });
            setStatus('Generating… this may take up to a minute.', false);
            onGenerate({
                prompt: prompt,
                style_code: activeStyle,
                side: side,
                file: file,
                rightsConfirmed: Boolean(file && rightsEl && rightsEl.checked),
                brief: briefPayload,
            })
                .catch((err) => {
                    setStatus(err && err.message ? err.message : 'Generation failed.', true);
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    }

    if (asisBtn && typeof onPrintAsIs === 'function') {
        asisBtn.addEventListener('click', () => {
            if (asisBtn.disabled || busy) {
                return;
            }
            const file = selectedFile();
            if (!file) {
                setStatus('Please choose a JPEG, PNG, or WebP image.', true);
                return;
            }
            if (!assertFileWithinLimit(file)) {
                return;
            }
            if (!rightsEl || !rightsEl.checked) {
                setStatus('Please confirm you have the rights to use this image.', true);
                return;
            }
            const side = /** @type {'front'|'back'} */ (store.getState().activeSide || 'front');
            setBusy(true, {
                title: 'Fitting your photo…',
                detail: 'This usually takes a few seconds.',
                button: asisBtn,
                buttonLabel: 'Fitting…',
            });
            setStatus('Fitting to print area…', false);
            onPrintAsIs({ file: file, side: side, rightsConfirmed: true })
                .catch((err) => {
                    setStatus(err && err.message ? err.message : 'Could not print as-is.', true);
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    }

    store.subscribe((state) => {
        if (quotaEl && state.quota) {
            const q = /** @type {{ remaining?: number, limit?: number }} */ (state.quota);
            quotaEl.textContent = 'Daily AI credits: ' + (q.remaining ?? '—') + ' / ' + (q.limit ?? '—');
        }
        const locked = busy || Boolean(state.busy);
        if (generateBtn) {
            generateBtn.disabled = locked;
        }
        if (suggestBtn) {
            suggestBtn.disabled = locked;
        }
        if (quickBtn) {
            quickBtn.disabled = locked;
        }
        if (promptEl instanceof HTMLTextAreaElement) {
            promptEl.disabled = locked;
        }
        if (fileEl instanceof HTMLInputElement) {
            fileEl.disabled = locked;
        }
        if (rightsEl instanceof HTMLInputElement) {
            rightsEl.disabled = locked;
        }
        if (attachBtn instanceof HTMLButtonElement) {
            attachBtn.disabled = locked;
        }
        if (removeBtn instanceof HTMLButtonElement) {
            removeBtn.disabled = locked;
        }
        root.querySelectorAll('[data-bag-style]').forEach((chip) => {
            if (chip instanceof HTMLButtonElement) {
                chip.disabled = locked;
            }
        });
        syncAsIsEnabled();
    });

    syncAsIsEnabled();

    /**
     * @returns {Promise<Record<string, unknown>>}
     */
    function runSuggest() {
        const prompt = promptEl ? String(promptEl.value || '').trim() : '';
        const file = selectedFile();
        if (!prompt && !file) {
            setStatus(emptyNeedIdea, true);
            return Promise.reject(new Error(emptyNeedIdea));
        }
        if (file && !assertFileWithinLimit(file)) {
            return Promise.reject(new Error('Please upload an image under ' + maxUploadMb + ' MB.'));
        }
        if (file && (!rightsEl || !rightsEl.checked)) {
            setStatus('Please confirm you have the rights to use this image.', true);
            return Promise.reject(new Error('Please confirm you have the rights to use this image.'));
        }
        const side = /** @type {'front'|'back'} */ (store.getState().activeSide || 'front');
        setBusy(true, {
            title: 'Planning your design…',
            detail: 'This usually takes a few seconds.',
            button: suggestBtn,
            buttonLabel: 'Planning…',
        });
        setStatus('Suggesting a design plan…', false);
        return onSuggestBrief({
            prompt: prompt,
            style_code: activeStyle,
            side: side,
            has_reference: Boolean(file),
        })
            .then((brief) => {
                applyBrief(brief);
                setStatus('Review the design plan, then Generate print.', false);
                return brief;
            })
            .finally(() => {
                setBusy(false);
            });
    }

    /**
     * @returns {File|null}
     */
    function selectedFile() {
        const files = fileEl && fileEl.files;
        return files && files[0] ? files[0] : null;
    }

    /**
     * @param {File} file
     * @returns {boolean}
     */
    function isAllowedImage(file) {
        const type = String(file.type || '').toLowerCase();
        if (type === 'image/jpeg' || type === 'image/png' || type === 'image/webp') {
            return true;
        }
        const name = String(file.name || '').toLowerCase();
        return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
    }

    /**
     * @param {string} message
     */
    function setCardError(message) {
        if (!(ideaErrorEl instanceof HTMLElement)) {
            return;
        }
        if (!message) {
            ideaErrorEl.textContent = '';
            ideaErrorEl.hidden = true;
            return;
        }
        ideaErrorEl.textContent = message;
        ideaErrorEl.hidden = false;
    }

    function revokeThumb() {
        if (thumbUrl) {
            URL.revokeObjectURL(thumbUrl);
            thumbUrl = null;
        }
        if (thumbEl instanceof HTMLImageElement) {
            thumbEl.removeAttribute('src');
        }
    }

    /**
     * @param {File} file
     * @returns {boolean}
     */
    function assignFile(file) {
        if (!(fileEl instanceof HTMLInputElement)) {
            return false;
        }
        try {
            const transfer = new DataTransfer();
            transfer.items.add(file);
            fileEl.files = transfer.files;
            return true;
        } catch {
            return false;
        }
    }

    function clearPhoto() {
        if (fileEl instanceof HTMLInputElement) {
            fileEl.value = '';
        }
        revokeThumb();
        if (filenameEl) {
            filenameEl.textContent = '';
        }
        pane.classList.remove('has-photo');
        if (rightsEl instanceof HTMLInputElement) {
            rightsEl.checked = false;
        }
        setCardError('');
        syncAsIsEnabled();
    }

    /**
     * @param {File} file
     */
    function paintPhoto(file) {
        revokeThumb();
        thumbUrl = URL.createObjectURL(file);
        if (thumbEl instanceof HTMLImageElement) {
            thumbEl.src = thumbUrl;
        }
        if (filenameEl) {
            filenameEl.textContent = file.name || 'Photo';
        }
        pane.classList.add('has-photo');
        if (rightsEl instanceof HTMLInputElement) {
            rightsEl.checked = false;
        }
        setCardError('');
        syncAsIsEnabled();
    }

    /**
     * @param {File} file
     * @param {boolean} assignToInput
     * @returns {boolean}
     */
    function acceptFile(file, assignToInput) {
        if (!isAllowedImage(file)) {
            clearPhoto();
            setCardError('Please choose a JPEG, PNG, or WebP image.');
            return false;
        }
        if (file.size > maxUploadBytes) {
            clearPhoto();
            setCardError('Please upload an image under ' + maxUploadMb + ' MB.');
            return false;
        }
        if (assignToInput && !assignFile(file)) {
            setCardError('Could not attach that photo. Please try again.');
            return false;
        }
        paintPhoto(file);
        return true;
    }

    function bindIdeaCardDrag() {
        if (!(ideaCard instanceof HTMLElement)) {
            return;
        }

        /**
         * @param {DragEvent} event
         */
        function isFileDrag(event) {
            const types = event.dataTransfer ? Array.from(event.dataTransfer.types || []) : [];
            return types.indexOf('Files') !== -1;
        }

        ideaCard.addEventListener('dragover', (event) => {
            if (busy || !isFileDrag(event)) {
                return;
            }
            event.preventDefault();
            ideaCard.classList.add('is-dragover');
        });

        ideaCard.addEventListener('dragleave', (event) => {
            const related = event.relatedTarget;
            if (related instanceof Node && ideaCard.contains(related)) {
                return;
            }
            ideaCard.classList.remove('is-dragover');
        });

        ideaCard.addEventListener('drop', (event) => {
            ideaCard.classList.remove('is-dragover');
            if (busy || !event.dataTransfer) {
                return;
            }
            event.preventDefault();
            const file = event.dataTransfer.files && event.dataTransfer.files[0]
                ? event.dataTransfer.files[0]
                : null;
            if (file) {
                acceptFile(file, true);
            }
        });
    }

    /**
     * @param {File} file
     */
    function assertFileWithinLimit(file) {
        if (file.size <= maxUploadBytes) {
            return true;
        }
        setStatus('Please upload an image under ' + maxUploadMb + ' MB.', true);
        return false;
    }

    function syncAsIsEnabled() {
        if (!asisBtn) {
            return;
        }
        asisBtn.disabled = busy || Boolean(store.getState().busy) || !selectedFile();
    }

    /**
     * @param {HTMLButtonElement|null} btn
     * @param {boolean} loading
     * @param {string} [loadingLabel]
     */
    function setActionLoading(btn, loading, loadingLabel) {
        if (!(btn instanceof HTMLButtonElement)) {
            return;
        }
        if (!btn.dataset.idleHtml) {
            btn.dataset.idleHtml = btn.innerHTML;
        }
        if (loading) {
            const label = loadingLabel || 'Working…';
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>' + label;
            btn.setAttribute('aria-busy', 'true');
            return;
        }
        btn.innerHTML = btn.dataset.idleHtml;
        btn.removeAttribute('aria-busy');
    }

    /**
     * @param {boolean} nextBusy
     * @param {{ title?: string, detail?: string, button?: HTMLButtonElement|null, buttonLabel?: string }} [meta]
     */
    function setBusy(nextBusy, meta) {
        busy = nextBusy;
        if (busy && overlay && meta && meta.title) {
            overlay.show('generate', meta.title, meta.detail || '');
        } else if (!busy && overlay) {
            overlay.hide('generate');
        }

        if (busy && meta && meta.button instanceof HTMLButtonElement) {
            loadingBtn = meta.button;
            setActionLoading(loadingBtn, true, meta.buttonLabel || 'Working…');
        } else if (!busy && loadingBtn) {
            setActionLoading(loadingBtn, false);
            loadingBtn = null;
        }

        if (generateBtn) {
            generateBtn.disabled = busy;
        }
        if (suggestBtn) {
            suggestBtn.disabled = busy;
        }
        if (quickBtn) {
            quickBtn.disabled = busy;
        }
        if (promptEl instanceof HTMLTextAreaElement) {
            promptEl.disabled = busy;
        }
        if (fileEl instanceof HTMLInputElement) {
            fileEl.disabled = busy;
        }
        if (rightsEl instanceof HTMLInputElement) {
            rightsEl.disabled = busy;
        }
        if (attachBtn instanceof HTMLButtonElement) {
            attachBtn.disabled = busy;
        }
        if (removeBtn instanceof HTMLButtonElement) {
            removeBtn.disabled = busy;
        }
        root.querySelectorAll('[data-bag-style]').forEach((chip) => {
            if (chip instanceof HTMLButtonElement) {
                chip.disabled = busy;
            }
        });
        syncAsIsEnabled();
    }

    /**
     * @param {string} message
     * @param {boolean} isError
     */
    function setStatus(message, isError) {
        if (!statusEl) {
            return;
        }
        statusEl.textContent = message;
        statusEl.classList.toggle('text-danger', isError);
        statusEl.classList.toggle('text-muted', !isError);
        if (isError && typeof statusEl.scrollIntoView === 'function') {
            statusEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }

    /**
     * @param {Record<string, unknown>} brief
     */
    function applyBrief(brief) {
        currentBrief = brief;
        if (!briefHost) {
            return;
        }
        briefHost.classList.remove('d-none');
        briefHost.hidden = false;
        const plan = /** @type {Record<string, unknown>} */ (brief.plan || {});
        if (summaryEl) {
            summaryEl.value = String(plan.summary || '');
        }
        if (notesEl) {
            notesEl.value = String(plan.notes_for_image_model || '');
        }
        if (plateEl) {
            plateEl.checked = String(plan.plate_policy || 'remove_flat_plate') === 'remove_flat_plate';
        }
        paintBriefChips();
    }

    function paintBriefChips() {
        if (!briefHost || !currentBrief || !currentBrief.plan) {
            return;
        }
        const plan = /** @type {Record<string, unknown>} */ (currentBrief.plan);
        briefHost.querySelectorAll('[data-brief-field]').forEach((group) => {
            const field = group.getAttribute('data-brief-field');
            const current = field ? String(plan[field] || '') : '';
            group.querySelectorAll('[data-brief-value]').forEach((btn) => {
                btn.classList.toggle('is-active', btn.getAttribute('data-brief-value') === current);
            });
        });
    }

    /**
     * @returns {Record<string, unknown>|null}
     */
    function syncBriefFromDom() {
        if (!currentBrief || !currentBrief.plan) {
            return null;
        }
        const plan = /** @type {Record<string, unknown>} */ (currentBrief.plan);
        if (summaryEl) {
            plan.summary = String(summaryEl.value || '').trim();
        }
        if (notesEl) {
            plan.notes_for_image_model = String(notesEl.value || '').trim();
        }
        if (plateEl) {
            plan.plate_policy = plateEl.checked ? 'remove_flat_plate' : 'keep_as_generated';
        }
        currentBrief.plan = plan;
        return currentBrief;
    }

    /**
     * Phase 11.3 — refill prompt / style / brief from a history design.
     * @param {Record<string, unknown>} design
     * @returns {{ needsSuggest: boolean, prompt: string, style_code: string|null, side: 'front'|'back' }}
     */
    function loadFromDesign(design) {
        const prompt = String(design.prompt || '').trim();
        const styleCode = design.style_code ? String(design.style_code) : null;
        const side = design.side === 'back' ? 'back' : 'front';

        if (promptEl) {
            promptEl.value = prompt;
        }

        activeStyle = styleCode;
        root.querySelectorAll('[data-bag-style]').forEach((el) => {
            el.classList.toggle('is-active', el.getAttribute('data-bag-style') === activeStyle);
        });

        const snapshot = design.design_brief;
        const hasBrief = briefEnabled
            && snapshot
            && typeof snapshot === 'object'
            && snapshot.plan
            && typeof snapshot.plan === 'object';

        if (hasBrief) {
            applyBrief(/** @type {Record<string, unknown>} */ (JSON.parse(JSON.stringify(snapshot))));
            setStatus('Design plan loaded — edit and Generate print for a new version.', false);
            return { needsSuggest: false, prompt: prompt, style_code: styleCode, side: side };
        }

        currentBrief = null;
        if (briefHost) {
            briefHost.classList.add('d-none');
            briefHost.hidden = true;
        }
        setStatus(
            briefEnabled
                ? 'No saved plan on this design — suggesting a fresh plan…'
                : 'Prompt restored. Suggest a design plan or Generate print.',
            false,
        );

        return {
            needsSuggest: Boolean(briefEnabled && (prompt || selectedFile())),
            prompt: prompt,
            style_code: styleCode,
            side: side,
        };
    }

    /**
     * Scroll Design plan into view after Edit plan / Suggest.
     */
    function focusBriefPanel() {
        if (!briefHost || briefHost.hidden || briefHost.classList.contains('d-none')) {
            if (promptEl && typeof promptEl.focus === 'function') {
                promptEl.focus();
            }
            return;
        }
        if (typeof briefHost.scrollIntoView === 'function') {
            briefHost.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }

    return {
        setStatus,
        applyBrief,
        loadFromDesign,
        focusBriefPanel,
        isBriefEnabled() {
            return briefEnabled;
        },
        setQuota(payload) {
            if (quotaEl && payload) {
                quotaEl.textContent = 'Daily AI credits: ' + (payload.remaining ?? '—') + ' / ' + (payload.limit ?? '—');
            }
        },
    };
}
